import { createFileRoute, Link } from '@tanstack/react-router';
import { CustomerList } from '@/components/customers/CustomerList';
import { toast } from 'sonner';
import { customerApi } from '@/lib/api/customers';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, ExternalLink, Users } from 'lucide-react';
import { jobsApi } from '@/lib/api/jobs.api';
import type { AssignedCustomer } from '@repo/types';
import { format } from 'date-fns';

export const Route = createFileRoute('/admin/jobs/$id/candidates')({
  component: RelatedCandidates,
});

function RelatedCandidates() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('all');

  // Fetch assigned customers
  const { data: assignedCustomers = [], isLoading: isLoadingAssigned, error: assignedError } = useQuery({
    queryKey: ['job', id, 'assigned-customers'],
    queryFn: () => jobsApi.getAssignedCustomers(id),
    enabled: activeTab === 'assigned' && !!id,
  });

  const handleAssignJob = async (customerId: string) => {
    try {
      await customerApi.assignJob(customerId, id);
      toast.success('Job assigned to customer successfully');
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['job', id, 'assigned-customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer', customerId, 'assigned-jobs'] });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to assign job to customer';
      toast.error(message);
      console.error('Error assigning job:', error);
    }
  };

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsContent value="all">
          <CustomerList
            jobId={id}
            showMatchScore={true}
            onAssignJob={handleAssignJob}
          />
        </TabsContent>

        <TabsContent value="assigned" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Assigned Customers ({assignedCustomers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingAssigned ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : assignedError ? (
                <div className="text-center py-12 text-destructive">
                  <p className="text-sm font-medium">Failed to load assigned customers</p>
                  <p className="text-xs mt-2 text-muted-foreground">
                    {assignedError instanceof Error ? assignedError.message : 'Unknown error occurred'}
                  </p>
                </div>
              ) : assignedCustomers.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-sm">No customers have been assigned to this job yet.</p>
                  <p className="text-xs mt-2">Switch to "All Candidates" to assign customers.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {assignedCustomers.map((assignment: AssignedCustomer) => (
                    <div
                      key={assignment.recommendationId}
                      className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Link
                              to="/admin/customers/$id"
                              params={{ id: assignment.customerId }}
                              className="font-semibold hover:underline truncate"
                            >
                              {assignment.customerName}
                            </Link>
                            <Badge
                              variant={
                                assignment.recommendationStatus === 'applied'
                                  ? 'default'
                                  : assignment.recommendationStatus === 'viewed'
                                  ? 'secondary'
                                  : 'outline'
                              }
                              className="text-xs capitalize shrink-0"
                            >
                              {assignment.recommendationStatus}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {assignment.customerEmail && (
                              <span className="truncate">{assignment.customerEmail}</span>
                            )}
                            {assignment.customerLocation && (
                              <>
                                <span>•</span>
                                <span>{assignment.customerLocation}</span>
                              </>
                            )}
                            {assignment.leadsRemaining !== undefined && (
                              <>
                                <span>•</span>
                                <span>{assignment.leadsRemaining} leads remaining</span>
                              </>
                            )}
                          </div>
                          {assignment.reason && (
                            <p className="text-xs text-muted-foreground italic mt-1">
                              Reason: {assignment.reason}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            {assignment.assignedByName && (
                              <span>Assigned by {assignment.assignedByName}</span>
                            )}
                            {assignment.assignedAt && (
                              <span>
                                {format(new Date(assignment.assignedAt), 'MMM dd, yyyy')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Link
                          to="/admin/customers/$id"
                          params={{ id: assignment.customerId }}
                        >
                          <Button variant="ghost" size="sm">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
